import React from 'react';
import { CheckCircle, XCircle, Clock, Calendar } from 'lucide-react';
import { Label } from '../../ui/label';

const EstimateStatus = ({ estimate }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, value) => {
    if (status === 'completed' || value === true) {
      return (
        <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-1 rounded-full text-sm">
          <CheckCircle className="w-3 h-3" />
          {typeof value === 'string' ? value : 'Completed'}
        </span>
      );
    }
    if (status === 'pending' || value === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full text-sm">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-gray-500 bg-gray-100 px-2 py-1 rounded-full text-sm">
        <XCircle className="w-3 h-3" />
        {typeof value === 'string' ? value : 'Not sent'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-gray-600">Proposal Sent</Label>
            {getStatusBadge(estimate.proposal_sent ? 'completed' : 'not_sent', estimate.proposal_sent)}
          </div>
          <div className="flex justify-between items-center">
            <Label className="text-gray-600">Accepted</Label>
            {getStatusBadge(
              estimate.accepted ? 'completed' : 'pending',
              estimate.accepted ? 'Accepted' : 'Pending'
            )}
          </div>
          <div className="flex justify-between items-center">
            <Label className="text-gray-600">Contract Signed</Label>
            {getStatusBadge(
              estimate.contract_signed ? 'completed' : 'not_sent',
              estimate.contract_signed ? 'Signed' : 'Not signed'
            )}
          </div>
        </div>
      </div>

      {/* Timeline Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          Timeline
        </h2>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-600 text-sm">Created</Label>
            <p className="text-gray-900 font-medium">{formatDate(estimate.created_at)}</p>
          </div>
          {estimate.updated_at && (
            <div>
              <Label className="text-gray-600 text-sm">Last Updated</Label>
              <p className="text-gray-900 font-medium">{formatDate(estimate.updated_at)}</p>
            </div>
          )}
          {estimate.scheduled_install_date && (
            <div>
              <Label className="text-gray-600 text-sm">Scheduled Install</Label>
              <p className="text-gray-900 font-medium text-green-700">
                {formatDate(estimate.scheduled_install_date)}
              </p>
            </div>
          )}
          {!estimate.scheduled_install_date && (
            <div>
              <Label className="text-gray-600 text-sm">Scheduled Install</Label>
              <p className="text-gray-500 text-sm">Not set</p>
            </div>
          )}
        </div>
      </div>

      {/* Contract Link if available */}
      {estimate.contract_url && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract</h2>
          <a
            href={estimate.contract_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm"
          >
            View Contract Document →
          </a>
        </div>
      )}
    </div>
  );
};

export default EstimateStatus;
